import React from 'react';

/**
 * MapPreview component for displaying location on map
 * Uses OpenStreetMap static tiles to avoid React Strict Mode issues with Leaflet
 *
 * @param {{ lat: number, lon: number }} coordinates - GPS coordinates to display
 * @param {number} height - Component height in pixels (default 300)
 */
export function MapPreview({ coordinates, height = 300 }) {
  if (!coordinates?.lat || !coordinates?.lon) {
    return (
      <div
        className="bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 border-2 border-dashed border-gray-300"
        style={{ height }}
      >
        <div className="text-center">
          <div className="text-2xl mb-2">&#128205;</div>
          <p>Paste a Google Maps URL to see preview</p>
        </div>
      </div>
    );
  }

  // Use OpenStreetMap static map image
  // Format: https://staticmap.openstreetmap.de/staticmap.php?center={lat},{lon}&zoom=16&size={w}x{h}&markers={lat},{lon},red-pushpin
  const mapWidth = 600;
  const mapHeight = height;
  const zoom = 16;

  // Static map URL from OpenStreetMap
  const staticMapUrl = `https://staticmap.openstreetmap.de/staticmap.php?center=${coordinates.lat},${coordinates.lon}&zoom=${zoom}&size=${mapWidth}x${mapHeight}&markers=${coordinates.lat},${coordinates.lon},red-pushpin`;

  return (
    <div
      className="rounded-lg overflow-hidden relative bg-gray-100"
      style={{ height }}
    >
      <img
        src={staticMapUrl}
        alt={`Map showing location at ${coordinates.lat}, ${coordinates.lon}`}
        className="w-full h-full object-cover"
        onError={(e) => {
          // Fallback if static map fails - show coordinates
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
      <div
        className="absolute inset-0 items-center justify-center bg-green-50 border-2 border-green-200 rounded-lg"
        style={{ display: 'none' }}
      >
        <div className="text-center text-green-700">
          <div className="text-3xl mb-2">&#9989;</div>
          <p className="font-semibold">Location Set</p>
          <p className="text-sm mt-1">
            {coordinates.lat.toFixed(6)}, {coordinates.lon.toFixed(6)}
          </p>
        </div>
      </div>
      {/* Coordinates overlay */}
      <div className="absolute bottom-2 left-2 bg-white/90 px-2 py-1 rounded text-xs text-gray-600 shadow">
        {coordinates.lat.toFixed(6)}, {coordinates.lon.toFixed(6)}
      </div>
    </div>
  );
}

export default MapPreview;
